import {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';

import { unstable_batchedUpdates } from 'react-dom';
import cx from 'classnames';
import { SvgIcon, makeStyles, useTheme } from '@material-ui/core';
import { spacing, palette } from '@material-ui/system';

import * as icons from 'assets/icons';
import * as mono from 'assets/icons/mono';
import { getArray, snakeCaseToCamelCase } from 'utils/helpers';
import withProps from 'hooks/withProps';
import SvgLoader from 'components/SvgLoader';

const IconComponents = {};

const useStyles = makeStyles((theme) => ({
  root: ({ sizeAnchor, transform, fontSize, relation, ...rest }) => {
    const colors = palette({ theme, ...rest });
    const spacings = spacing({ theme, ...rest });

    return {
      ...colors,
      ...spacings,
      fontSize,
      transform,
      width: sizeAnchor === 'width' ? '1em' : `${relation}em`,
      height: sizeAnchor === 'height' ? '1em' : `${relation}em`,
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shortest,
      }),
    };
  },
  colors: ({ colors }) => {
    const result = {};

    colors.forEach((c, colorIndex) => {
      const targetColor = palette({ theme, color: c })?.color || c;

      result[`& [data-color-index="${colorIndex}"]`] = {
        fill: targetColor,
      };
    });
    return result;
  },
}));

const IconBase = forwardRef((props, ref) => {
  const {
    src,
    rotate = 0,
    flipX,
    flipY,
    className,
    color: initialColor = 'inherit',
    colors: initialColors,
    fontSize = 'medium',
    sizeAnchor = 'width',
    viewBox: initialViewBox = '0 0 24 24',
    ...rest
  } = props;

  const mountRef = useRef({ mounted: false });
  const [viewBox, setViewBox] = useState(initialViewBox);
  const [sizeRelation, setSizeRelation] = useState(1);
  const [nativeColors, setNativeColors] = useState();
  const theme = useTheme();

  const color = useMemo(() => {
    const c = initialColor;
    return theme.palette[c]?.main || c;
  }, [initialColor, theme]);

  const colors = useMemo(() => {
    return getArray(nativeColors)
      .map((c, colorIndex) => {
        const commonColor = color !== 'inherit' ? color : undefined;
        return commonColor || initialColors?.[colorIndex] || c;
      })
      .map((c) => {
        return theme.palette[c]?.main || c;
      });
  }, [initialColors, nativeColors, theme, color]);

  const transform = useMemo(() => {
    return [
      `rotate(${rotate}deg)`,
      flipX && 'scaleX(-1)',
      flipY && 'scaleY(-1)',
    ]
      .filter(Boolean)
      .join(' ');
  }, [rotate, flipX, flipY]);

  const nativeFontSize = useMemo(() => {
    if (['small', 'medium', 'large', 'inherit', undefined].includes(fontSize)) {
      return fontSize;
    }
    return 'inherit';
  }, [fontSize]);

  const styleFontSize = useMemo(() => {
    if (Number.isFinite(fontSize)) {
      return fontSize;
    }
    return undefined;
  }, [fontSize]);

  const [restStyles, restProps] = useMemo(() => {
    const props = {};
    const styles = {};

    Object.entries(rest).forEach(([name, v]) => {
      if (name.startsWith('on')) {
        props[name] = v;
      } else {
        styles[name] = v;
      }
    });
    return [styles, props];
  }, [rest]);

  const classes = useStyles({
    transform,
    sizeAnchor,
    fontSize: styleFontSize,
    relation: sizeRelation,
    color,
    colors,
    ...restStyles,
  });

  const handleSourceLoading = useCallback((source) => {
    const [, width] = getArray(source.match(/width="(.*?)"/im));
    const [, height] = getArray(source.match(/height="(.*?)"/im));
    const [, currentViewBox] = getArray(source.match(/viewBox="(.*?)"/im));
    const w = +width;
    const h = +height;
    const sizeCoif = h / w;
    const colorsDict = [];

    const template = document.createElement('template');
    template.innerHTML = source.trim();
    const element = template.content.firstChild;
    const coloredElements = Array.from(element.querySelectorAll('[fill]'));

    coloredElements.forEach((ce) => {
      const elementColor = ce.getAttribute('fill');

      if (!colorsDict.includes(elementColor)) {
        colorsDict.push(elementColor);
      }
      const colorIndex = colorsDict.indexOf(elementColor);
      ce.setAttribute('data-color-index', colorIndex);
    });
    setTimeout(() => {
      unstable_batchedUpdates(() => {
        if (!mountRef.current.mounted) {
          return;
        }
        setViewBox(currentViewBox);
        setSizeRelation(sizeCoif);
        setNativeColors(colorsDict);
      });
    }, 4);

    return element.outerHTML;
  }, []);

  const InlineIcon = useMemo(() => {
    return withProps(SvgLoader, {
      src,
      preProcessor: handleSourceLoading,
    });
  }, [handleSourceLoading, src]);

  useEffect(() => {
    const mount = mountRef.current;
    mount.mounted = true;

    return () => {
      mount.mounted = false;
    };
  }, []);

  return (
    <SvgIcon
      ref={ref}
      viewBox={viewBox}
      fontSize={nativeFontSize}
      component={InlineIcon}
      className={cx(classes.root, classes.colors, className)}
      {...restProps}
    />
  );
});

const createIconComponent = (src) => {
  return withProps(IconBase, { src });
};

const Icon = forwardRef((props, ref) => {
  const { name, ...rest } = props;
  const TargetIcon = IconComponents[name];

  if (!name) {
    return <IconBase ref={ref} {...rest} />;
  }
  return !TargetIcon ? null : <TargetIcon {...rest} ref={ref} />;
});

Object.entries(icons).forEach(([name, path]) => {
  const ComponentName = snakeCaseToCamelCase(name, true);
  const Component = createIconComponent(path);
  Component.is_mono = !!mono[name];
  IconComponents[name] = Component;
  Icon[ComponentName] = Component;
});

export default Icon;
